import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { UseFormReturn } from "react-hook-form";
import { useAllCustomers } from "../../hooks/useCustomer";
import { useState } from "react";
import AsyncSelect from "../../components/common/Select/AsyncSelect";
import { ConfigureFederatedAuthValues } from "./FederatedAuthForm";
import MetadataDocumentInput from "./MetadataDocumentInput";
import TextField from "@mui/material/TextField";
import FormGroup from "@mui/material/FormGroup";
import {
  Alert,
  AlertTitle,
  FormHelperText,
  IconButton,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

export interface FederatedAuthFormViewProps {
  form: UseFormReturn<ConfigureFederatedAuthValues>;
  showIdpInitiatedUrl?: boolean;
  savedProviderName?: string | null;
}

const buildIdpInitiatedUrl = (providerName: string) =>
  `${window.location.origin}/sso/start/${providerName}`;

const FederatedAuthFormView = ({
  form,
  showIdpInitiatedUrl = false,
  savedProviderName,
}: FederatedAuthFormViewProps) => {
  const [readyToQueryCustomers, setReadyToQueryCustomers] =
    useState<boolean>(false);
  const { register, control, formState } = form;
  const { errors, isValid, isDirty, isSubmitting } = formState;
  const {
    data: customers,
    error: customerError,
    isLoading: areCustomersLoading,
  } = useAllCustomers(readyToQueryCustomers);

  const idpInitiatedUrl =
    showIdpInitiatedUrl && savedProviderName
      ? buildIdpInitiatedUrl(savedProviderName)
      : null;

  const handleCopy = async () => {
    if (!idpInitiatedUrl) return;
    await navigator.clipboard.writeText(idpInitiatedUrl);
  };

  return (
    <Box className="w-3/4 pt-2 m-auto flex flex-col space-y-4">
      <FormGroup>
        <TextField
          {...register("name")}
          label="Name"
          error={!!errors.name}
          size="small"
          required
        />
        {errors.name && (
          <FormHelperText error>{errors.name.message}</FormHelperText>
        )}
      </FormGroup>
      <FormGroup>
        <AsyncSelect
          label="Customer"
          control={control}
          name="customer"
          options={customers ?? []}
          setIsReady={setReadyToQueryCustomers}
          error={!!customerError || !!errors.customer}
          loading={areCustomersLoading}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) =>
            option.customerId === value.customerId
          }
          required
        />
        {errors.customer && (
          <FormHelperText error>{errors.customer.message}</FormHelperText>
        )}
      </FormGroup>
      <MetadataDocumentInput className="mt-2" />
      <Button
        type="submit"
        variant="contained"
        disabled={!isValid || !isDirty || isSubmitting}
        fullWidth
      >
        Save Changes
      </Button>
      {idpInitiatedUrl && (
        <Alert severity="info" icon={false}>
          <AlertTitle>IdP-initiated single sign-on</AlertTitle>
          Configure your IdP to redirect users here for IdP-initiated logins:
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            mt={1}
            sx={{ wordBreak: "break-all" }}
          >
            <code>{idpInitiatedUrl}</code>
            <Tooltip title="Copy URL">
              <IconButton
                size="small"
                onClick={handleCopy}
                aria-label="Copy IdP-initiated URL"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Alert>
      )}
    </Box>
  );
};

export default FederatedAuthFormView;
